import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
    Table,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { DATABASE_VEHICULE_EDIT, DATABASE_VEHICULE_MANAGER_PAGE } from "../../routes/routeConstants";
import { useDispatch, useSelector } from "react-redux";
import { vehiculeActions } from "./vehiculeSlice";
import { printVehicleRecap } from "./vehiclePrintUtils";
import { isVehiclePhoto, resolveDocumentUrl } from "./documentUrl";

const getVehicleTitle = (vehicle) => {
    const title = [vehicle?.marque, vehicle?.modele].filter(Boolean).join(" ");
    return title || vehicle?.numChassis || vehicle?.immatriculation || "Vehicule";
};

const formatAmount = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    const amount = Number(value);
    if (Number.isNaN(amount)) return value;
    return new Intl.NumberFormat("fr-MA", { maximumFractionDigits: 2 }).format(amount);
};

function VehiculePage() {
    const { t } = useTranslation("translation");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { vehicules = [], isFetching } = useSelector(state => state.vehicule);
    const [record, setRecord] = useState({ open: false, data: null });
    const [ficheVehicle, setFicheVehicle] = useState(null);
    const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], index: 0, title: "" });
    const [filters, setFilters] = useState({
        search: "",
        reform: "",
        carburant: "",
        marque: "",
    });

    useEffect(() => {
        load();
    }, []);

    const load = () => {
        dispatch(vehiculeActions.search({
            criteria: {},
            page: 0,
            size: 100,
        }));
    };

    const handleDelete = (vehicle) => {
        setRecord({ open: true, data: vehicle });
    };

    const confirmDelete = () => {
        setRecord({ ...record, open: false });
        dispatch(vehiculeActions.delete({
            id: record.data.id,
            onSuccess: load,
            onError: (error) => console.log(error),
        }));
    };

    const optionSets = useMemo(() => {
        const values = (key) => [...new Set((vehicules || []).map(item => item?.[key]).filter(Boolean))].sort();
        return {
            marques: values("marque"),
            carburants: values("carburant"),
            reforms: values("reform"),
        };
    }, [vehicules]);

    const filteredVehicles = useMemo(() => {
        const search = filters.search.trim().toLowerCase();
        return (vehicules || []).filter((vehicle) => {
            const searchable = [
                vehicle?.marque,
                vehicle?.modele,
                vehicle?.type,
                vehicle?.genre,
                vehicle?.carburant,
                vehicle?.numChassis,
                vehicle?.immatriculation,
                vehicle?.proprietere,
            ].filter(Boolean).join(" ").toLowerCase();

            return (!search || searchable.includes(search))
                && (!filters.reform || vehicle?.reform === filters.reform)
                && (!filters.carburant || vehicle?.carburant === filters.carburant)
                && (!filters.marque || vehicle?.marque === filters.marque);
        });
    }, [vehicules, filters]);

    const clearFilters = () => {
        setFilters({ search: "", reform: "", carburant: "", marque: "" });
    };

    const vehiclePhotos = (vehicle) => (vehicle?.documents || []).filter(isVehiclePhoto);
    const vehicleDocuments = (vehicle) => (vehicle?.documents || []).filter((doc) => doc?.type !== "PHOTO");
    const getPhotoAlt = (photo, vehicle) => photo?.filenameUser || photo?.filename || `Photo ${getVehicleTitle(vehicle)}`;
    const openPhotoViewer = (vehicle, index = 0) => {
        const photos = vehiclePhotos(vehicle);
        if (photos.length === 0) return;
        setPhotoViewer({ open: true, photos, index, title: getVehicleTitle(vehicle) });
    };
    const closePhotoViewer = () => setPhotoViewer(prev => ({ ...prev, open: false }));

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card className="page-compact-header mb-3 border-0 shadow-sm">
                        <CardBody>
                            <Row className="align-items-center g-3">
                                <Col lg={5}>
                                    <div className="page-inline-breadcrumb mb-2">
                                        <span>{t("database")}</span>
                                        <i className="ri-arrow-right-s-line"></i>
                                        <strong>{t("vehicule")}</strong>
                                    </div>
                                    <h4 className="mb-1">{t("vehicule")}</h4>
                                    <p className="text-muted mb-0">Recherche, filtres et gestion rapide du parc.</p>
                                </Col>
                                <Col lg={7}>
                                    <div className="d-flex flex-wrap justify-content-lg-end gap-2">
                                        <Button color="primary" onClick={() => navigate(DATABASE_VEHICULE_MANAGER_PAGE)}>
                                            <i className="ri-add-line me-1"></i>
                                            Nouveau vehicule
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    <Card className="mb-4 border-0 shadow-sm">
                        <CardBody>
                            <Row className="g-3 align-items-end">
                                <Col lg={4}>
                                    <label className="form-label">Recherche</label>
                                    <InputGroup>
                                        <InputGroupText>
                                            <i className="ri-search-line"></i>
                                        </InputGroupText>
                                        <Input
                                            value={filters.search}
                                            placeholder="Marque, chassis, immatriculation..."
                                            onChange={(event) => setFilters(prev => ({ ...prev, search: event.target.value }))}
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={3} lg={2}>
                                    <label className="form-label">{t("vehiculef.marque")}</label>
                                    <Input
                                        type="select"
                                        value={filters.marque}
                                        onChange={(event) => setFilters(prev => ({ ...prev, marque: event.target.value }))}
                                    >
                                        <option value="">Toutes</option>
                                        {optionSets.marques.map(value => <option key={value} value={value}>{value}</option>)}
                                    </Input>
                                </Col>
                                <Col md={3} lg={2}>
                                    <label className="form-label">{t("vehiculef.carburant")}</label>
                                    <Input
                                        type="select"
                                        value={filters.carburant}
                                        onChange={(event) => setFilters(prev => ({ ...prev, carburant: event.target.value }))}
                                    >
                                        <option value="">Tous</option>
                                        {optionSets.carburants.map(value => <option key={value} value={value}>{value}</option>)}
                                    </Input>
                                </Col>
                                <Col md={3} lg={2}>
                                    <label className="form-label">{t("vehiculef.reform")}</label>
                                    <Input
                                        type="select"
                                        value={filters.reform}
                                        onChange={(event) => setFilters(prev => ({ ...prev, reform: event.target.value }))}
                                    >
                                        <option value="">Toutes</option>
                                        {optionSets.reforms.map(value => <option key={value} value={value}>{value}</option>)}
                                    </Input>
                                </Col>
                                <Col md={3} lg={2}>
                                    <Button color="light" className="w-100" onClick={clearFilters}>
                                        <i className="ri-filter-off-line me-1"></i>
                                        Reinitialiser
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {isFetching ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                        </div>
                    ) : filteredVehicles.length === 0 ? (
                        <Card className="border-0 shadow-sm">
                            <CardBody className="text-center py-5">
                                <i className="ri-car-line display-5 text-muted"></i>
                                <h5 className="mt-3">Aucun vehicule trouve</h5>
                                <p className="text-muted mb-3">Ajustez les filtres ou ajoutez un nouveau vehicule.</p>
                                <Button color="primary" onClick={() => navigate(DATABASE_VEHICULE_MANAGER_PAGE)}>Nouveau vehicule</Button>
                            </CardBody>
                        </Card>
                    ) : (
                        <Row className="g-4">
                            {filteredVehicles.map((vehicle) => {
                                const photos = vehiclePhotos(vehicle);
                                const coverPhoto = photos[0];

                                return (
                                    <Col xl={4} md={6} key={vehicle.id}>
                                        <Card className="h-100 border-0 shadow-sm vehicle-card">
                                            <button
                                                type="button"
                                                className={`vehicle-card-photo ${coverPhoto ? "" : "is-empty"}`}
                                                style={coverPhoto ? { backgroundImage: `url("${resolveDocumentUrl(coverPhoto)}")` } : undefined}
                                                onClick={() => openPhotoViewer(vehicle)}
                                                disabled={!coverPhoto}
                                                aria-label={coverPhoto ? `Voir les photos de ${getVehicleTitle(vehicle)}` : "Aucune photo disponible"}
                                            >
                                                {coverPhoto ? (
                                                    <span className="vehicle-photo-count">
                                                        <i className="ri-image-line me-1"></i>
                                                        {photos.length}
                                                    </span>
                                                ) : (
                                                    <span className="vehicle-photo-empty">
                                                        <i className="ri-image-add-line"></i>
                                                        Aucune photo
                                                    </span>
                                                )}
                                            </button>
                                            <CardBody>
                                            <div className="d-flex justify-content-between gap-3 mb-3">
                                                <div>
                                                    <h5 className="mb-1">{getVehicleTitle(vehicle)}</h5>
                                                    <div className="text-muted small">{vehicle.numChassis || "Chassis non renseigne"}</div>
                                                </div>
                                                <Badge color={vehicle.reform === "ECONOMIQUE" ? "warning" : "danger"} className="align-self-start">
                                                    {vehicle.reform || "-"}
                                                </Badge>
                                            </div>

                                            <div className="d-flex flex-wrap gap-2 mb-3">
                                                {vehicle.carburant && <Badge color="light" className="text-dark">{vehicle.carburant}</Badge>}
                                                {vehicle.puissFiscale && <Badge color="light" className="text-dark">{vehicle.puissFiscale} CV</Badge>}
                                                {vehicle.nbrCylindres && <Badge color="light" className="text-dark">{vehicle.nbrCylindres} cyl.</Badge>}
                                            </div>

                                            <Row className="g-3 mb-3">
                                                <Col xs={6}>
                                                    <div className="text-muted small">Immatriculation</div>
                                                    <div className="fw-semibold">{vehicle.immatriculation || "-"}</div>
                                                </Col>
                                                <Col xs={6}>
                                                    <div className="text-muted small">Prix achat</div>
                                                    <div className="fw-semibold">{formatAmount(vehicle.prixAchat)} MAD</div>
                                                </Col>
                                                <Col xs={6}>
                                                    <div className="text-muted small">Mise en circulation</div>
                                                    <div className="fw-semibold">{vehicle.firstMec || "-"}</div>
                                                </Col>
                                                <Col xs={6}>
                                                    <div className="text-muted small">Proprietaire</div>
                                                    <div className="fw-semibold text-truncate">{vehicle.proprietere || "-"}</div>
                                                </Col>
                                            </Row>

                                            <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                                                <Link to={DATABASE_VEHICULE_EDIT.replace(":id", vehicle.id)} className="btn btn-sm btn-light">
                                                    <i className="mdi mdi-pencil me-1"></i>
                                                    Modifier
                                                </Link>
                                                <div className="d-flex gap-2">
                                                    <Button size="sm" color="light" onClick={() => setFicheVehicle(vehicle)}>
                                                        <i className="ri-file-list-3-line me-1"></i>
                                                        Fiche
                                                    </Button>
                                                    <Button size="sm" color="light" onClick={() => printVehicleRecap(vehicle)}>
                                                        <i className="ri-printer-line me-1"></i>
                                                        Imprimer
                                                    </Button>
                                                    <Button size="sm" color="light" className="text-danger" onClick={() => handleDelete(vehicle)}>
                                                        <i className="mdi mdi-trash-can me-1"></i>
                                                        Supprimer
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                );
                            })}
                        </Row>
                    )}
                </Container>
            </div>

            <Modal isOpen={record.open} toggle={() => setRecord({ ...record, open: false })} backdrop="static">
                <ModalHeader toggle={() => setRecord({ ...record, open: false })}>
                    {t("text.confirmation")}
                </ModalHeader>
                <ModalBody>
                    <p>{t("text.msgDelete")}</p>
                    <ModalFooter>
                        <Button type="button" color="light" onClick={() => setRecord({ ...record, open: false })}>{t("actions.close")}</Button>
                        <Button type="button" color="primary" onClick={confirmDelete}>{t("actions.confirm")}</Button>
                    </ModalFooter>
                </ModalBody>
            </Modal>

            <Modal size="lg" isOpen={Boolean(ficheVehicle)} toggle={() => setFicheVehicle(null)}>
                <ModalHeader toggle={() => setFicheVehicle(null)}>Fiche vehicule</ModalHeader>
                <ModalBody>
                    {ficheVehicle && (
                        <div className="vehicle-fiche">
                            <Row className="g-3 mb-3">
                                {[
                                    ["Marque", ficheVehicle.marque],
                                    ["Modele", ficheVehicle.modele],
                                    ["Type", ficheVehicle.type],
                                    ["Genre", ficheVehicle.genre],
                                    ["Carburant", ficheVehicle.carburant],
                                    ["N chassis", ficheVehicle.numChassis],
                                    ["Immatriculation", ficheVehicle.immatriculation],
                                    ["Proprietaire", ficheVehicle.proprietere],
                                    ["Premiere MEC", ficheVehicle.firstMec],
                                    ["MEC Maroc", ficheVehicle.mecMaroc],
                                    ["Date sinistre", ficheVehicle.dateSinistre],
                                    ["Compteur", ficheVehicle.compteur],
                                    ["Prix achat", `${formatAmount(ficheVehicle.prixAchat)} MAD`],
                                    ["Valeur marche", `${formatAmount(ficheVehicle.valMarche)} MAD`],
                                    ["Epave", `${formatAmount(ficheVehicle.epave)} MAD`],
                                    ["Devis", `${formatAmount(ficheVehicle.devis)} MAD`],
                                    ["Reforme", ficheVehicle.reform],
                                ].map(([label, value]) => (
                                    <Col md={4} key={label}>
                                        <div className="text-muted small">{label}</div>
                                        <div className="fw-semibold">{value || "-"}</div>
                                    </Col>
                                ))}
                            </Row>

                            {vehiclePhotos(ficheVehicle).length > 0 && (
                                <>
                                    <h6 className="mt-4">Photos</h6>
                                    <div className="vehicle-photo-preview-grid">
                                        {vehiclePhotos(ficheVehicle).map((photo, index) => (
                                            <button
                                                type="button"
                                                className="vehicle-photo-thumb"
                                                onClick={() => openPhotoViewer(ficheVehicle, index)}
                                                key={photo.id || photo.path}
                                            >
                                                <img src={resolveDocumentUrl(photo)} alt={getPhotoAlt(photo, ficheVehicle)} />
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}

                            <h6 className="mt-4">Documents</h6>
                            {vehicleDocuments(ficheVehicle).length > 0 ? (
                                <Table responsive size="sm">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Fichier</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vehicleDocuments(ficheVehicle).map((doc) => (
                                            <tr key={doc.id}>
                                                <td>{doc.type}</td>
                                                <td>{doc.filenameUser || doc.filename}</td>
                                                <td><Button size="sm" color="light" onClick={() => window.open(resolveDocumentUrl(doc), "_blank")}>Voir</Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted">Aucun document attache.</p>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setFicheVehicle(null)}>Fermer</Button>
                    {ficheVehicle && <Button color="primary" onClick={() => printVehicleRecap(ficheVehicle)}>Imprimer recap</Button>}
                </ModalFooter>
            </Modal>

            {photoViewer.open && photoViewer.photos.length > 0 && (
                <Lightbox
                    mainSrc={resolveDocumentUrl(photoViewer.photos[photoViewer.index])}
                    nextSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + 1) % photoViewer.photos.length])}
                    prevSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + photoViewer.photos.length - 1) % photoViewer.photos.length])}
                    imageTitle={photoViewer.title}
                    imageCaption={photoViewer.photos[photoViewer.index].filenameUser || photoViewer.photos[photoViewer.index].filename}
                    onCloseRequest={closePhotoViewer}
                    onMovePrevRequest={() => setPhotoViewer(prev => ({
                        ...prev,
                        index: (prev.index + prev.photos.length - 1) % prev.photos.length,
                    }))}
                    onMoveNextRequest={() => setPhotoViewer(prev => ({
                        ...prev,
                        index: (prev.index + 1) % prev.photos.length,
                    }))}
                    enableZoom={true}
                />
            )}
        </React.Fragment>
    );
}

export default VehiculePage;
